import './styles.css';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { createIcons, Menu, X, Search, ArrowRight, ChevronDown, Landmark, PanelsTopLeft, BadgeCheck, MapPin, Phone, Mail, ExternalLink, CircleCheck, WalletCards, CreditCard, FileText, Download, Scale } from 'lucide';
import { content, categoryLabels } from './content.js';

const app = document.querySelector('#app');
const asset = (name) => `/assets/${name}`;

const images = {
  logo: asset('finbox-logo-and-mark.svg'),
  logoWhite: asset('finbox-logo-and-mark.svg'),
  hero: asset('002-80b565_7cc1cd4acbd345b6a24367d4b8c5b243~mv2.png-Frame-1171277391.png'),
  team: asset('003-80b565_eb21ba694db04cbba9ccc15881ecfab8~mv2.jpg-happy-business-people-celebrating-success-while-stacking-hands-motivational-meet.jpg'),
  about: asset('009-80b565_6e742a308d56428498b54877cec99190~mv2.png-about-us.png'),
};

const banks = [
  { code: '11500', logo: '03.png', hy: 'ԱՄԻՕ Բանկ', en: 'AMIO Bank', ru: 'АМИО Банк' },
  { code: '11800', logo: '05.png', hy: 'ԱյԴի Բանկ', en: 'IDBank', ru: 'АйДи Банк' },
  { code: '15100', logo: '09.png', hy: 'ԱրարատԲանկ', en: 'AraratBank', ru: 'АраратБанк' },
  { code: '15700', logo: '07.png', hy: 'Ամերիաբանկ', en: 'Ameriabank', ru: 'Америабанк' },
  { code: '16000', logo: '04.png', hy: 'ՎՏԲ-Հայաստան Բանկ', en: 'VTB Armenia Bank', ru: 'Банк ВТБ Армения' },
  { code: '16300', logo: '11.png', hy: 'Հայէկոնոմբանկ', en: 'Armeconombank', ru: 'Армэкономбанк' },
  { code: '16600', logo: '06.png', hy: 'Էվոկաբանկ', en: 'Evocabank', ru: 'Эвокабанк', zeroFee: true },
  { code: '19300', logo: '10.png', hy: 'Կոնվերս Բանկ', en: 'Converse Bank', ru: 'Конверс Банк', zeroFee: true },
  { code: '20500', logo: '02.png', hy: 'Ինեկոբանկ', en: 'Inecobank', ru: 'Инекобанк' },
  { code: '20800', logo: '17.png', hy: 'Մելլաթ Բանկ', en: 'Mellat Bank', ru: 'Банк Меллат' },
  { code: '21400', logo: '16.png', hy: 'Բիբլոս Բանկ Արմենիա', en: 'Byblos Bank Armenia', ru: 'Библос Банк Армения' },
  { code: '22000', logo: '12.png', hy: 'ԱԿԲԱ Բանկ', en: 'ACBA Bank', ru: 'АКБА Банк' },
  { code: '22300', logo: '../050-80b565_25655225f25a4e3d99f7ab8dcf2d32ef~mv2.png-Artsaxbank.png', hy: 'Արցախբանկ', en: 'Artsakhbank', ru: 'Арцахбанк' },
  { code: '24100', logo: '13.png', hy: 'Յունիբանկ', en: 'Unibank', ru: 'Юнибанк' },
  { code: '24700', logo: '08.png', hy: 'Արդշինբանկ', en: 'Ardshinbank', ru: 'Ардшинбанк' },
  { code: '25000', logo: '15.png', hy: 'Արմսվիսբանկ', en: 'ArmSwissBank', ru: 'Армсвисбанк' },
  { code: '26200', logo: '../064-80b565_977d126131434a738a66fd61cdf82a12~mv2.png-Fastbank.png', hy: 'Ֆասթ Բանկ', en: 'Fast Bank', ru: 'Фаст Банк' },
];

let rawContent = null;
let assetManifest = [];
let serviceGroups = [];
let currentLocale = 'hy';
const showAtmMap = false;

const slugMap = {
  '': 'home',
  'about-us': 'about',
  atms: 'atms',
  's-projects-side-by-side': 'services',
  services: 'services',
  'services/banks': 'banks',
  'services/banks/card-refill': 'cardRefill',
  'services/banks/account-refill': 'accountRefill',
  becomeapartner: 'partner',
  faq: 'faq',
  financesystemmediator: 'mediator',
  'public-offer': 'offer',
  reports: 'reports',
  'consumer-rights': 'consumerRights',
  'useful-links': 'usefulLinks',
  'cookie-policy': 'cookiePolicy',
  search: 'search',
};

function parseRoute() {
  const parts = location.pathname.split('/').filter(Boolean);
  const locale = parts[0] === 'hy' || parts[0] === 'ru' ? parts.shift() : 'en';
  const slug = parts.join('/');
  if (/^services\/banks\/\d+$/.test(slug)) return { locale, page: 'bankDetails' };
  return { locale, page: slugMap[slug] || 'notFound' };
}

function pathFor(page, locale = currentLocale) {
  const prefix = locale === 'en' ? '' : `/${locale}`;
  if (page === 'bankDetails') return `${prefix}/services/banks/${location.pathname.split('/').filter(Boolean).at(-1)}`;
  const slugs = { home: '', about: 'about-us', atms: 'atms', services: 'services', banks: 'services/banks', cardRefill: 'services/banks/card-refill', accountRefill: 'services/banks/account-refill', partner: 'becomeapartner', faq: 'faq', mediator: 'financesystemmediator', offer: 'public-offer', reports: 'reports', consumerRights: 'consumer-rights', usefulLinks: 'useful-links', cookiePolicy: 'cookie-policy', search: 'search' };
  return `${prefix}/${slugs[page] || ''}`.replace(/\/+/g, '/');
}

function navigate(path) {
  history.pushState({}, '', path);
  window.scrollTo({ top: 0, behavior: 'instant' });
  render();
}

function iconRefresh() {
  createIcons({ icons: { Menu, X, Search, ArrowRight, ChevronDown, Landmark, PanelsTopLeft, BadgeCheck, MapPin, Phone, Mail, ExternalLink, CircleCheck, WalletCards, CreditCard, FileText, Download, Scale } });
}

function header(t, page) {
  return `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="${pathFor('home')}" data-link aria-label="FINBOX ${t.common.home}">
          <img src="${images.logo}" alt="FINBOX" />
        </a>
        <nav class="desktop-nav" aria-label="${t.ui.mainNavigation}">
          ${navLink('about', t.nav.about, page)}
          ${navLink('atms', t.nav.atms, page)}
          ${navLink('services', t.nav.services, page)}
          ${navLink('partner', t.nav.partner, page)}
        </nav>
        <div class="header-tools">
          <a class="icon-button" href="${pathFor('search')}" data-link aria-label="${t.nav.search}" title="${t.nav.search}"><i data-lucide="search"></i></a>
          <div class="language-picker">
            <button class="language-button" type="button" aria-expanded="false">${t.code}<i data-lucide="chevron-down"></i></button>
            <div class="language-menu" role="menu">
              ${['hy', 'en', 'ru'].map((locale) => `<button type="button" data-locale="${locale}" class="${locale === currentLocale ? 'active' : ''}">${content[locale].code}</button>`).join('')}
            </div>
          </div>
          <button class="icon-button mobile-menu-button" type="button" aria-label="${t.ui.menu}" aria-expanded="false"><i data-lucide="menu"></i></button>
        </div>
      </div>
      <div class="mobile-nav" aria-hidden="true">
        ${navLink('about', t.nav.about, page)}
        ${navLink('atms', t.nav.atms, page)}
        ${navLink('services', t.nav.services, page)}
        ${navLink('partner', t.nav.partner, page)}
        ${navLink('faq', t.faq.title, page)}
      </div>
    </header>`;
}

function navLink(page, label, activePage) {
  const active = activePage === page || (page === 'services' && ['banks', 'bankDetails', 'cardRefill', 'accountRefill'].includes(activePage));
  return `<a href="${pathFor(page)}" data-link class="${active ? 'active' : ''}">${label}</a>`;
}

function footer(t) {
  return `
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <img src="${images.logoWhite}" alt="FINBOX" />
          <p>${t.common.regulated}</p>
        </div>
        <div class="footer-column">
          <h2>${t.common.general}</h2>
          <a href="${pathFor('about')}" data-link>${t.nav.about}</a>
          <a href="${pathFor('services')}" data-link>${t.nav.services}</a>
          <a href="${pathFor('partner')}" data-link>${t.nav.partner}</a>
          <a href="${pathFor('faq')}" data-link>${t.faq.title}</a>
        </div>
        <div class="footer-column">
          <h2>${t.common.information}</h2>
          <a href="${pathFor('reports')}" data-link>${t.reports.title}</a>
          <a href="${pathFor('consumerRights')}" data-link>${t.consumerRights.title}</a>
          <a href="${pathFor('usefulLinks')}" data-link>${t.usefulLinks.title}</a>
          <a href="${pathFor('cookiePolicy')}" data-link>${t.cookiePolicy.title}</a>
          <a href="${pathFor('mediator')}" data-link>${t.mediator.title}</a>
          <a href="${pathFor('offer')}" data-link>${t.offer.title}</a>
        </div>
        <div class="footer-column footer-contact">
          <h2>${t.common.contact}</h2>
          <p><i data-lucide="map-pin"></i><span>${t.common.address}</span></p>
          <a href="mailto:info@finbox.am"><i data-lucide="mail"></i><span>info@finbox.am</span></a>
          <a href="tel:+37460802222"><i data-lucide="phone"></i><span>+374 60 802222</span></a>
        </div>
      </div>
      <div class="footer-bottom"><span>© ${new Date().getFullYear()} ${t.common.copyrightName}</span><span>${t.common.country}</span></div>
    </footer>`;
}

function pageShell(t, page, body) {
  return `${header(t, page)}<main>${body}</main>${footer(t)}`;
}

function buttonLink(page, label, secondary = false) {
  return `<a class="button ${secondary ? 'button-secondary' : ''}" href="${pathFor(page)}" data-link>${label}<i data-lucide="arrow-right"></i></a>`;
}

function homePage(t) {
  return `
    <section class="hero">
      <div class="hero-inner">
        <div class="hero-copy reveal">
          <p class="eyebrow">${t.home.eyebrow}</p>
          <h1>${t.home.title}</h1>
          <p class="hero-lead">${t.home.lead}</p>
          ${buttonLink('partner', t.nav.partner)}
        </div>
        <div class="hero-art reveal reveal-delay"><img src="${images.hero}" alt="FINBOX ATM service" /></div>
      </div>
    </section>
    <section class="trust-strip" aria-label="FINBOX facts">
      <div><strong>400+</strong><span>${t.nav.services}</span></div>
      <div><strong>24/7</strong><span>${t.nav.atms}</span></div>
      <div><strong>CBA</strong><span>${t.common.regulated}</span></div>
    </section>
    <section class="vision-section section">
      <div class="section-inner vision-grid">
        <div class="vision-copy reveal-on-scroll">
          <p class="section-number">01</p><p class="eyebrow">FINBOX</p>
          <h2>${t.home.visionTitle}</h2><p class="large-copy">${t.home.vision}</p>
          ${buttonLink('about', t.common.learnMore, true)}
        </div>
        <figure class="vision-image reveal-on-scroll"><img src="${images.team}" alt="FINBOX team" /></figure>
      </div>
    </section>
    <section class="services-preview section">
      <div class="section-inner">
        <div class="section-heading reveal-on-scroll"><div><p class="section-number">02</p><p class="eyebrow">${t.nav.services}</p></div><div><h2>${t.home.servicesTitle}</h2><p>${t.home.servicesLead}</p></div></div>
        <div class="service-feature-list">
          ${t.home.services.map(([title, text, icon], index) => `<article class="service-feature reveal-on-scroll"><span>0${index + 1}</span><i data-lucide="${icon}"></i><div><h3>${title}</h3><p>${text}</p></div></article>`).join('')}
        </div>
        <div class="section-action">${buttonLink('services', t.common.viewServices)}</div>
      </div>
    </section>
    <section class="values-section section">
      <div class="section-inner">
        <div class="section-heading light reveal-on-scroll"><div><p class="section-number">03</p><p class="eyebrow">FINBOX</p></div><div><h2>${t.home.valuesTitle}</h2></div></div>
        <div class="values-grid">${t.home.values.map(([title, text], index) => `<article class="value reveal-on-scroll"><span>0${index + 1}</span><h3>${title}</h3><p>${text}</p></article>`).join('')}</div>
      </div>
    </section>
    ${partnerCta(t)}`;
}

function partnerCta(t) {
  return `<section class="partner-cta"><div class="partner-cta-image"><img src="${images.about}" alt="${t.ui.partnershipImageAlt}" /></div><div class="partner-cta-copy reveal-on-scroll"><p class="eyebrow">FINBOX NETWORK</p><h2>${t.home.ctaTitle}</h2><p>${t.home.ctaText}</p>${buttonLink('partner', t.nav.partner)}</div></section>`;
}

function innerHero(eyebrow, title, intro, image = images.about) {
  return `<section class="inner-hero"><div class="inner-hero-copy reveal"><p class="eyebrow">${eyebrow}</p><h1>${title}</h1><p>${intro}</p></div><div class="inner-hero-image reveal reveal-delay"><img src="${image}" alt="${title}" /></div></section>`;
}

function aboutPage(t) {
  return `${innerHero('FINBOX', t.about.title, t.about.intro)}
    <section class="about-details section"><div class="section-inner about-detail-grid">
      <article class="reveal-on-scroll"><p class="section-number">01</p><h2>${t.about.missionTitle}</h2><p>${t.about.mission}</p></article>
      <article class="reveal-on-scroll"><p class="section-number">02</p><h2>${t.about.goalTitle}</h2><p>${t.about.goal}</p></article>
    </div></section>${partnerCta(t)}`;
}

function atmsPage(t) {
  return `${innerHero('FINBOX ATM', t.atms.title, t.atms.intro, images.hero)}
    <section class="atm-list section"><div class="section-inner narrow">
      ${t.atms.items.map(([title, text], index) => `<article class="atm-item reveal-on-scroll"><span>${String(index + 1).padStart(2, '0')}</span><div><h2>${title}</h2><p>${text}</p></div><i data-lucide="circle-check"></i></article>`).join('')}
    </div></section>${showAtmMap ? atmMapSection(t) : ''}${partnerCta(t)}`;
}

function atmMapSection(t) {
  const mapCopy = t.atmMap;
  return `<section class="atm-map-section section"><div class="section-inner wide">
      <div class="atm-map-heading reveal-on-scroll"><div><p class="eyebrow">FINBOX NETWORK</p><h2>${mapCopy.title}</h2><p>${mapCopy.intro}</p></div><span class="atm-map-count"><strong>2</strong>${mapCopy.count}</span></div>
      <div class="atm-map-shell reveal-on-scroll"><div id="atm-map" aria-label="${mapCopy.title}"></div><div class="atm-map-locations">
        ${mapCopy.locations.map((location, index) => `<button type="button" data-atm-index="${index}"><span>${String(index + 1).padStart(2, '0')}</span><div><strong>${location.name}</strong><small>${location.address}</small></div><i data-lucide="map-pin"></i></button>`).join('')}
      </div></div>
      <p class="map-attribution-note">© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors</p>
    </div></section>`;
}

function faqPage(t) {
  return `${innerHero('FINBOX', t.faq.title, t.faq.intro, images.hero)}
    <section class="faq-section section"><div class="section-inner narrow faq-list">
      ${t.faq.items.map(([question, answer], index) => `<article class="faq-item ${index === 0 ? 'open' : ''}"><button type="button" aria-expanded="${index === 0}"><span>${question}</span><i data-lucide="chevron-down"></i></button><div class="faq-answer"><p>${answer}</p>${index === 1 ? buttonLink('services', t.common.viewServices, true) : ''}</div></article>`).join('')}
    </div></section>`;
}

function partnerPage(t) {
  return `<section class="partner-page"><div class="partner-info"><p class="eyebrow">FINBOX NETWORK</p><h1>${t.partner.title}</h1><p class="large-copy">${t.partner.intro}</p><div class="contact-lines"><a href="tel:+37460802222"><i data-lucide="phone"></i><span>+374 60 802222</span></a><a href="mailto:info@finbox.am"><i data-lucide="mail"></i><span>info@finbox.am</span></a></div></div>
    <div class="partner-form-wrap"><form class="partner-form" novalidate>
      <div class="form-row"><label>${t.partner.firstName}<input name="firstName" autocomplete="given-name" required /><small></small></label><label>${t.partner.lastName}<input name="lastName" autocomplete="family-name" required /><small></small></label></div>
      <label>${t.partner.email}<input name="email" type="email" autocomplete="email" required /><small></small></label>
      <label>${t.partner.phone}<input name="phone" type="tel" autocomplete="tel" /></label>
      <label>${t.partner.message}<textarea name="message" rows="6" required></textarea><small></small></label>
      <button class="button" type="submit">${t.partner.send}<i data-lucide="arrow-right"></i></button><p class="form-status" role="status"></p>
    </form></div></section>`;
}

function mediatorPage(t) {
  return `<section class="document-page section"><div class="section-inner document-grid"><div><a class="back-link" href="${pathFor('home')}" data-link>← ${t.common.back}</a><p class="eyebrow">FINBOX</p><h1>${t.mediator.title}</h1></div><div class="document-content">
    <section><h2>${t.mediator.addressLabel}</h2><p>${t.mediator.address}</p><p>${t.mediator.phone}<br/>${t.mediator.fax}</p><a href="mailto:info@fsm.am">info@fsm.am</a></section>
    <section><h2>${t.mediator.hoursTitle}</h2><p>${t.mediator.hours}</p></section>
    <section><h2>${t.mediator.website}</h2><a class="text-link" href="https://fsm.am" target="_blank" rel="noreferrer">www.fsm.am <i data-lucide="external-link"></i></a></section>
  </div></div></section>`;
}

function offerPage(t) {
  const originalPath = currentLocale === 'en' ? '' : `/${currentLocale}`;
  return `<section class="document-page section"><div class="section-inner document-grid"><div><p class="eyebrow">FINBOX</p><h1>${t.offer.title}</h1></div><div class="document-content"><a class="offer-row" href="https://atm.finbox.am${originalPath}/public-offer" target="_blank" rel="noreferrer"><span><i data-lucide="wallet-cards"></i>${t.offer.item}</span><span>${t.offer.open}<i data-lucide="external-link"></i></span></a></div></div></section>`;
}

function rawServicePage(locale) {
  const suffix = locale === 'en' ? '/s-projects-side-by-side' : `/${locale}/s-projects-side-by-side`;
  return rawContent?.pages.find((page) => new URL(page.finalUrl).pathname === suffix);
}

function buildServiceGroups(locale) {
  const raw = rawServicePage(locale)?.text || [];
  const categories = categoryLabels[locale];
  const start = raw.findIndex((line) => line === categories[0]);
  const endMarkers = { hy: 'Ընդհանուր', en: 'General', ru: 'Общая' };
  let lines = raw.slice(start, raw.indexOf(endMarkers[locale], start));
  lines = lines.map((line) => decodeEntities(line.trim())).filter(Boolean);
  const logoFiles = assetManifest
    .map((item) => item.file.split('/').pop())
    .filter((file) => /^(0[1-9][0-9]|1[0-6][0-4])-/.test(file))
    .sort((a, b) => Number(a.slice(0, 3)) - Number(b.slice(0, 3)));

  const groups = [];
  let group = null;
  let logoIndex = 0;
  for (let i = 0; i < lines.length; i += 1) {
    let line = lines[i];
    if (categories.includes(line)) {
      group = { name: line, items: [] };
      groups.push(group);
      continue;
    }
    if (!group) continue;
    if (line === 'դրամ' || line === 'AMD' || line === 'руб.') {
      if (group.items.length) group.items.at(-1).detail += ` ${line}`;
      continue;
    }
    if (line === 'Հարկադիր' && lines[i + 1] === 'կատարման' && lines[i + 2] === 'ծառայություն') {
      line = 'Հարկադիր կատարման ծառայություն';
      i += 2;
    }
    if (line === 'Q. Termi' && lines[i + 1] === 'nal') {
      line = 'Q. Terminal';
      i += 1;
    }
    const isDetail = /^(Սպասարկման|Ծառայության|Վճարման|Service Fee|Payment Range|Стоимость|Диапазон|Комиссия|<|\(|\)|;|\d+[.,]?\d*\s*(AMD|դրամ|руб))/i.test(line);
    if (isDetail) {
      if (group.items.length) group.items.at(-1).detail = `${group.items.at(-1).detail} ${line}`.trim();
    } else {
      group.items.push({ name: line, detail: '', logo: logoFiles[logoIndex] ? asset(logoFiles[logoIndex]) : null });
      logoIndex += 1;
    }
  }
  return groups.filter((item) => item.items.length > 0);
}

function decodeEntities(value) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function servicesPage(t) {
  return `<section class="services-hero services-overview-hero"><div><p class="eyebrow">FINBOX</p><h1>${t.services.title}</h1><p>${t.services.intro}</p></div></section>
    <section class="service-category-section section"><div class="section-inner">
      <a class="service-category-card reveal-on-scroll" href="${pathFor('banks')}" data-link>
        <div class="service-category-icon"><i data-lucide="landmark"></i></div>
        <div class="service-category-copy"><span class="section-number">01</span><h2>${t.services.bankTitle}</h2><p>${t.services.bankDescription}</p></div>
        <span class="service-category-more">${t.services.more}<i data-lucide="arrow-right"></i></span>
      </a>
    </div></section>`;
}

function banksPage(t) {
  return `<section class="services-hero bank-list-hero"><div><a class="bank-back-link" href="${pathFor('services')}" data-link>${t.common.back}</a><p class="eyebrow">FINBOX</p><h1>${t.services.bankTitle}</h1><p>${t.services.bankListIntro}</p></div></section>
    <section class="bank-list-section section"><div class="section-inner wide">
      <div class="bank-grid">
        <a class="bank-card bank-tariff-card reveal-on-scroll" href="${pathFor('cardRefill')}" data-link><div class="bank-logo payment-logo-pair"><img src="${asset('visa-logo.svg')}" alt="Visa" /><img src="${asset('mastercard-logo.svg')}" alt="Mastercard" /></div><div><h2>${t.services.cardRefillTitle}</h2></div></a>
        <a class="bank-card bank-tariff-card reveal-on-scroll" href="${pathFor('accountRefill')}" data-link><div class="bank-logo bank-tariff-icon"><i data-lucide="landmark"></i></div><div><h2>${t.services.accountRefillTitle}</h2></div></a>
        ${banks.map((bank) => `<a class="bank-card ${['22300', '26200'].includes(bank.code) ? 'bank-card-blend-logo' : ''} reveal-on-scroll" href="${pathFor('banks')}/${bank.code}" data-link><div class="bank-logo"><img src="${asset(`easypay-banks/${bank.logo}`)}" alt="${bank[currentLocale]}" /></div><div><h2>${bank[currentLocale]}</h2></div></a>`).join('')}
      </div>
    </div></section>`;
}

function refillTariffsPage(t, type) {
  const isCard = type === 'card';
  const title = isCard ? t.services.cardRefillTitle : t.services.accountRefillTitle;
  const intro = isCard ? t.services.cardRefillIntro : t.services.accountRefillIntro;
  return `<section class="services-hero bank-list-hero"><div><a class="bank-back-link" href="${pathFor('banks')}" data-link>${t.common.back}</a><p class="eyebrow">FINBOX</p><h1>${title}</h1><p>${intro}</p></div></section>
    <section class="refill-tariffs-section section"><div class="section-inner wide"><div class="refill-rate-table" role="table" aria-label="${title}">
      <div class="refill-rate-row refill-rate-head" role="row"><span>${t.services.bank}</span><span>${t.services.commission}</span><span>${t.services.limit}</span></div>
      ${banks.map((bank) => `<div class="refill-rate-row" role="row"><div class="refill-bank"><img src="${asset(`easypay-banks/${bank.logo}`)}" alt=""/><strong>${bank[currentLocale]}</strong></div><div><ul>${t.services.feeTiers.map((tier) => `<li>${tier}</li>`).join('')}</ul><small>${t.services.minimumFee}: 200 ${t.services.amd}</small></div><div class="refill-limit"><strong>400,000 ${t.services.amd}</strong><small>${t.services.maximumTransaction}</small></div></div>`).join('')}
    </div></div></section>`;
}

function cardRefillPage(t) { return refillTariffsPage(t, 'card'); }
function accountRefillPage(t) { return refillTariffsPage(t, 'account'); }

function bankDetailsPage(t) {
  const code = location.pathname.split('/').filter(Boolean).at(-1);
  const bank = banks.find((item) => item.code === code);
  if (!bank) return notFoundPage(t);
  const paymentTypes = t.services.paymentTypes.slice(0, 3);
  return `<section class="bank-detail-hero"><div class="bank-detail-identity"><a class="bank-detail-back" href="${pathFor('banks')}" data-link>${t.common.back}</a><div class="bank-detail-logo"><img src="${asset(`easypay-banks/${bank.logo}`)}" alt="${bank[currentLocale]}" /></div><div><h1>${bank[currentLocale]}</h1><p>${t.services.detailIntro}</p></div></div></section>
    <section class="bank-detail-section section"><div class="section-inner wide">
      <div class="payment-table" role="table" aria-label="${bank[currentLocale]}">
        <div class="payment-row payment-head" role="row"><span>${t.services.paymentType}</span><span>${t.services.commission}</span><span>${t.services.limit}</span></div>
        ${paymentTypes.map((type, index) => { const free = bank.zeroFee && index < 3; return `<div class="payment-row" role="row"><div class="payment-type-cell"><span>${String(index + 1).padStart(2, '0')}</span><strong>${type}</strong></div><div class="payment-fee-cell ${free ? 'fee-free' : ''}">${free ? `<strong>0 ${t.services.amd}</strong>` : `<ul>${t.services.feeTiers.map((tier) => `<li>${tier}</li>`).join('')}</ul><small>${t.services.minimumFee}: 200 ${t.services.amd}</small>`}</div><div class="payment-limit-cell"><strong>400,000 ${t.services.amd}</strong><small>${t.services.maximumTransaction}</small></div></div>`; }).join('')}
      </div>
    </div></section>`;
}

function reportsPage(t) {
  return `<section class="reports-hero"><div class="reports-hero-inner"><p class="eyebrow">FINBOX</p><h1>${t.reports.title}</h1><p>${t.reports.intro}</p></div></section>
    <section class="reports-section section"><div class="section-inner wide">
      <div class="reports-grid">${t.reports.items.map((report) => {
        const body = `<div class="report-year">${report.year}</div><div class="report-card-bottom"><i data-lucide="file-text"></i><div><strong>${report.fileName}</strong><span>${report.size || t.reports.pending}</span></div><span class="report-download" title="${report.url ? t.reports.download : t.reports.pending}"><i data-lucide="download"></i></span></div>`;
        return report.url ? `<a class="report-card reveal-on-scroll" href="${report.url}" target="_blank" rel="noreferrer" download>${body}</a>` : `<article class="report-card report-card-pending reveal-on-scroll">${body}</article>`;
      }).join('')}</div>
    </div></section>`;
}

function consumerRightsPage(t) {
  return `<section class="legal-hero"><div><p class="eyebrow">FINBOX</p><h1>${t.consumerRights.title}</h1><p>${t.consumerRights.intro}</p></div></section>
    <section class="consumer-rights-section section"><div class="section-inner document-grid">
      <aside class="consumer-rights-aside"><i data-lucide="scale"></i><h2>${t.consumerRights.documentsTitle}</h2><p>${t.consumerRights.documentsIntro}</p>
        <div class="legal-document-list">${t.consumerRights.documents.map((document) => `<div class="legal-document-placeholder"><i data-lucide="file-text"></i><div><strong>${document}</strong><span>${t.consumerRights.pending}</span></div></div>`).join('')}</div>
      </aside>
      <article class="consumer-rights-content">
        <p>${t.consumerRights.paragraphs[0]}</p>
        <p>${t.consumerRights.paragraphs[1]} <a href="${pathFor('offer')}" data-link>${t.offer.title}</a>։</p>
        <section><h2>${t.consumerRights.acceptedTitle}</h2><ul>
          <li><i data-lucide="map-pin"></i><span>${t.common.address}</span></li>
          <li><i data-lucide="phone"></i><a href="tel:+37460802222">+374 60 802222</a></li>
          <li><i data-lucide="mail"></i><a href="mailto:info@finbox.am">info@finbox.am</a></li>
        </ul></section>
        <div class="consumer-note"><i data-lucide="circle-check"></i><p>${t.consumerRights.responseNote}</p></div>
      </article>
    </div></section>`;
}

function usefulLinksPage(t) {
  return `<section class="legal-hero"><div><p class="eyebrow">FINBOX</p><h1>${t.usefulLinks.title}</h1><p>${t.usefulLinks.intro}</p></div></section>
    <section class="useful-links-section section"><div class="section-inner narrow">
      <div class="useful-links-list">${t.usefulLinks.items.map(([name, description, url], index) => `<a class="useful-link-row reveal-on-scroll" href="${url}" target="_blank" rel="noreferrer"><span>${String(index + 1).padStart(2, '0')}</span><div><h2>${name}</h2><p>${description}</p></div><i data-lucide="external-link"></i></a>`).join('')}</div>
      <div class="external-links-note"><i data-lucide="circle-check"></i><p>${t.usefulLinks.disclaimer}</p></div>
    </div></section>`;
}

function cookiePolicyPage(t) {
  return `<section class="legal-hero"><div><p class="eyebrow">FINBOX</p><h1>${t.cookiePolicy.title}</h1><p>${t.cookiePolicy.intro}</p></div></section>
    <section class="policy-section section"><div class="section-inner document-grid">
      <aside class="policy-index"><p class="section-number">01</p><h2>${t.cookiePolicy.contentsTitle}</h2><ol>${t.cookiePolicy.sections.map((section, index) => `<li><a href="#cookie-${index + 1}">${section.title}</a></li>`).join('')}</ol></aside>
      <article class="policy-content">
        ${t.cookiePolicy.opening.map((paragraph) => `<p>${paragraph}</p>`).join('')}
        ${t.cookiePolicy.sections.map((section, index) => `<section id="cookie-${index + 1}"><span>${String(index + 1).padStart(2, '0')}</span><h2>${section.title}</h2>${section.paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join('')}</section>`).join('')}
      </article>
    </div></section>`;
}

function buildBankGroups(locale, t) {
  return [{
    name: t.services.title,
    items: banks.map((bank) => ({
      name: bank[locale],
      detail: '',
      logo: asset(`easypay-banks/${bank.logo}`),
      searchTerms: `${bank.hy} ${bank.en} ${bank.ru}`,
    })),
  }];
}

function renderServiceGroups(groups) {
  return groups.map((group, groupIndex) => `<section class="service-group" data-group="${groupIndex}"><div class="service-group-heading"><span>${String(groupIndex + 1).padStart(2, '0')}</span><h2>${group.name}</h2><strong>${group.items.length}</strong></div><div class="service-grid">${group.items.map((item) => `<article class="service-card" data-search="${`${item.name} ${item.detail} ${item.searchTerms || ''}`.toLocaleLowerCase()}">${item.logo ? `<div class="service-logo"><img loading="lazy" src="${item.logo}" alt="${item.name}" /></div>` : ''}<h3>${item.name}</h3>${item.detail ? `<p>${item.detail}</p>` : ''}</article>`).join('')}</div></section>`).join('');
}

function searchPage(t) {
  return `<section class="search-page section"><div class="section-inner narrow"><p class="eyebrow">FINBOX</p><h1>${t.search.title}</h1><p>${t.search.intro}</p><label class="global-search"><i data-lucide="search"></i><input autofocus type="search" placeholder="${t.search.placeholder}" /></label><div class="search-results"><p>${t.search.empty}</p></div></div></section>`;
}

function notFoundPage(t) {
  return `<section class="not-found section"><div><p class="eyebrow">404</p><h1>${t.ui.pageNotFound}</h1>${buttonLink('home', t.common.home)}</div></section>`;
}

function render() {
  const route = parseRoute();
  currentLocale = route.locale;
  const t = content[currentLocale];
  document.documentElement.lang = currentLocale;
  document.title = `${route.page === 'home' ? 'FINBOX' : (t[route.page]?.title || t.nav[route.page] || 'FINBOX')} | FINBOX`;
  const pages = { home: homePage, about: aboutPage, atms: atmsPage, services: servicesPage, banks: banksPage, bankDetails: bankDetailsPage, cardRefill: cardRefillPage, accountRefill: accountRefillPage, reports: reportsPage, consumerRights: consumerRightsPage, usefulLinks: usefulLinksPage, cookiePolicy: cookiePolicyPage, partner: partnerPage, faq: faqPage, mediator: mediatorPage, offer: offerPage, search: searchPage, notFound: notFoundPage };
  app.innerHTML = pageShell(t, route.page, pages[route.page](t));
  bindEvents(t, route.page);
  iconRefresh();
  requestAnimationFrame(observeReveals);
}

function bindEvents(t, page) {
  document.querySelectorAll('[data-link]').forEach((link) => link.addEventListener('click', (event) => { if (event.metaKey || event.ctrlKey) return; event.preventDefault(); navigate(link.getAttribute('href')); }));

  const languageButton = document.querySelector('.language-button');
  const languageMenu = document.querySelector('.language-menu');
  languageButton?.addEventListener('click', () => {
    const isOpen = languageButton.getAttribute('aria-expanded') === 'true';
    languageButton.setAttribute('aria-expanded', String(!isOpen));
    languageMenu.classList.toggle('open', !isOpen);
  });
  document.querySelectorAll('[data-locale]').forEach((button) => button.addEventListener('click', () => navigate(pathFor(page, button.dataset.locale))));

  const menuButton = document.querySelector('.mobile-menu-button');
  const mobileNav = document.querySelector('.mobile-nav');
  menuButton?.addEventListener('click', () => {
    const isOpen = menuButton.getAttribute('aria-expanded') === 'true';
    menuButton.setAttribute('aria-expanded', String(!isOpen));
    mobileNav.classList.toggle('open', !isOpen);
    mobileNav.setAttribute('aria-hidden', String(isOpen));
    menuButton.innerHTML = `<i data-lucide="${isOpen ? 'menu' : 'x'}"></i>`;
    iconRefresh();
  });

  document.querySelectorAll('.faq-item button').forEach((button) => button.addEventListener('click', () => {
    const item = button.closest('.faq-item');
    const next = !item.classList.contains('open');
    item.classList.toggle('open', next);
    button.setAttribute('aria-expanded', String(next));
  }));

  bindPartnerForm(t);
  bindServiceCatalog(t);
  bindGlobalSearch(t);
  if (page === 'atms') bindAtmMap(t);
}

function bindAtmMap(t) {
  const mapElement = document.querySelector('#atm-map');
  if (!mapElement) return;
  const mapCopy = t.atmMap;
  const locations = [
    { lat: 40.2760475, lng: 44.2953515, ...mapCopy.locations[0] },
    { lat: 40.1673966, lng: 44.5149408, ...mapCopy.locations[1] },
  ];
  const map = L.map(mapElement, { scrollWheelZoom: false, attributionControl: false });
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
  const markerIcon = L.divIcon({ className: 'finbox-map-marker', html: '<span><i></i></span>', iconSize: [42, 52], iconAnchor: [21, 50], popupAnchor: [0, -44] });
  const markers = locations.map((location) => L.marker([location.lat, location.lng], { icon: markerIcon }).addTo(map)
    .bindPopup(`<div class="finbox-popup"><span>${t.ui.atmLabel}</span><strong>${location.name}</strong><p>${location.address}</p><a href="https://www.openstreetmap.org/directions?to=${location.lat}%2C${location.lng}" target="_blank" rel="noreferrer">${mapCopy.route} →</a></div>`, { closeButton: false, offset: [0, -4] }));
  map.fitBounds(L.latLngBounds(locations.map(({ lat, lng }) => [lat, lng])), { padding: [55, 55] });
  document.querySelectorAll('[data-atm-index]').forEach((button) => button.addEventListener('click', () => {
    const index = Number(button.dataset.atmIndex);
    map.flyTo(markers[index].getLatLng(), index === 0 ? 15 : 16, { duration: 0.8 });
    markers[index].openPopup();
    document.querySelectorAll('[data-atm-index]').forEach((item) => item.classList.toggle('active', item === button));
  }));
  window.setTimeout(() => map.invalidateSize(), 100);
}

function bindPartnerForm(t) {
  const form = document.querySelector('.partner-form');
  if (!form) return;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    let valid = true;
    form.querySelectorAll('[required]').forEach((field) => {
      const small = field.parentElement.querySelector('small');
      let error = '';
      if (!field.value.trim()) error = t.partner.required;
      else if (field.type === 'email' && !/^\S+@\S+\.\S+$/.test(field.value)) error = t.partner.invalidEmail;
      field.classList.toggle('invalid', Boolean(error));
      if (small) small.textContent = error;
      if (error) valid = false;
    });
    if (!valid) return;
    const data = new FormData(form);
    const subject = encodeURIComponent(`${t.ui.partnerEmailSubject}: ${data.get('firstName')} ${data.get('lastName')}`);
    const body = encodeURIComponent(`${t.ui.formName}: ${data.get('firstName')} ${data.get('lastName')}\n${t.ui.formEmail}: ${data.get('email')}\n${t.ui.formPhone}: ${data.get('phone')}\n\n${data.get('message')}`);
    form.querySelector('.form-status').textContent = t.partner.ready;
    window.location.href = `mailto:info@finbox.am?subject=${subject}&body=${body}`;
  });
}

function bindServiceCatalog(t) {
  const searchInput = document.querySelector('.service-search input');
  if (!searchInput) return;
  let activeCategory = 'all';
  const apply = () => {
    const query = searchInput.value.trim().toLocaleLowerCase();
    let visibleCount = 0;
    document.querySelectorAll('.service-group').forEach((group) => {
      let groupCount = 0;
      group.querySelectorAll('.service-card').forEach((card) => {
        const visible = (activeCategory === 'all' || group.dataset.group === activeCategory) && (!query || card.dataset.search.includes(query));
        card.hidden = !visible;
        if (visible) { groupCount += 1; visibleCount += 1; }
      });
      group.hidden = groupCount === 0;
    });
    document.querySelector('.service-empty').hidden = visibleCount > 0;
  };
  searchInput.addEventListener('input', apply);
  document.querySelectorAll('.category-tabs button').forEach((button) => button.addEventListener('click', () => {
    activeCategory = button.dataset.category;
    document.querySelectorAll('.category-tabs button').forEach((item) => item.classList.toggle('active', item === button));
    apply();
  }));
}

function bindGlobalSearch(t) {
  const input = document.querySelector('.global-search input');
  if (!input) return;
  const routeEntries = [
    ['about', t.about.title, t.about.intro], ['atms', t.atms.title, t.atms.intro], ['services', t.services.title, t.services.intro], ['partner', t.partner.title, t.partner.intro], ['faq', t.faq.title, t.faq.intro], ['mediator', t.mediator.title, t.mediator.address], ['offer', t.offer.title, t.offer.item],
  ];
  const serviceEntries = buildBankGroups(currentLocale, t).flatMap((group) => group.items.map((item) => ['services', item.name, group.name]));
  const results = document.querySelector('.search-results');
  input.addEventListener('input', () => {
    const query = input.value.trim().toLocaleLowerCase();
    if (!query) { results.innerHTML = `<p>${t.search.empty}</p>`; return; }
    const matches = [...routeEntries, ...serviceEntries].filter(([, title, text]) => `${title} ${text}`.toLocaleLowerCase().includes(query)).slice(0, 30);
    results.innerHTML = matches.length ? matches.map(([page, title, text]) => `<a href="${pathFor(page)}" data-link><span>${title}</span><p>${text}</p><i data-lucide="arrow-right"></i></a>`).join('') : `<p>${t.search.noResults}</p>`;
    results.querySelectorAll('[data-link]').forEach((link) => link.addEventListener('click', (event) => { event.preventDefault(); navigate(link.getAttribute('href')); }));
    iconRefresh();
  });
}

function observeReveals() {
  document.querySelectorAll('.reveal').forEach((element) => element.classList.add('visible'));
  const observer = new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('visible'); observer.unobserve(entry.target); } }), { threshold: 0.12 });
  document.querySelectorAll('.reveal-on-scroll').forEach((element) => observer.observe(element));
}

window.addEventListener('popstate', render);

async function init() {
  try {
    [rawContent, assetManifest] = await Promise.all([
      fetch('/data/atm-finbox-content.json').then((response) => response.json()),
      fetch('/data/atm-finbox-images-manifest.json').then((response) => response.json()),
    ]);
  } catch (error) {
    console.error('Could not load exported FINBOX content.', error);
  }
  render();
}

init();
